import { browser } from "$app/env";

export interface UserAgentInfo {
    os?: string;
    browser?: string;
    bot?: string;
}

let cachedUaInfo: UserAgentInfo | null = null;

export function getUserAgentInfo(): UserAgentInfo {
    if (!browser || typeof navigator === 'undefined') return {};
    if (cachedUaInfo) return cachedUaInfo;

    const ua = navigator.userAgent || '';
    const info: UserAgentInfo = {};

    // 1. Detect bots / crawlers / AI agents / automation tools
    const botMatch = ua.match(
        /(Googlebot|bingbot|Baiduspider|YandexBot|DuckDuckBot|GPTBot|ChatGPT-User|ClaudeBot|PerplexityBot|Bytespider|CCBot|Applebot|facebookexternalhit|Twitterbot|LinkedInBot|Discordbot|TelegramBot|Slackbot|WhatsApp|AhrefsBot|SemrushBot|Lighthouse|HeadlessChrome|Playwright|Puppeteer|Selenium|PhantomJS|Scrapy|curl|Wget|python-requests|httpx|aiohttp|Go-http-client|node-fetch|axios|Google-Extended|Meta-ExternalAgent|cohere-ai|bot|crawler|spider)/i
    );

    let isAutomated = false;
    try {
        isAutomated = Boolean(navigator.webdriver);
    } catch {
        // Throwing an error is proof of automation
        isAutomated = true;
    }
    if (botMatch) {
        info.bot = botMatch[0];
    } else if (isAutomated) {
        info.bot = 'Headless-Webdriver';
    }

    // 2. Detect OS
    if (/iPhone|iPad|iPod/i.test(ua)) info.os = 'iOS';
    else if (/Android/i.test(ua)) info.os = 'Android';
    else if (/Windows/i.test(ua)) info.os = 'Windows';
    else if (/Macintosh|Mac OS X/i.test(ua)) info.os = 'macOS';
    else if (/Linux/i.test(ua)) info.os = 'Linux';
    else if (/CrOS/i.test(ua)) info.os = 'ChromeOS';

    // 3. Detect Browser Family (only if not a bot)
    if (!info.bot) {
        if (/Edg/i.test(ua)) info.browser = 'Edge';
        else if (/OPR|Opera/i.test(ua)) info.browser = 'Opera';
        else if (/SamsungBrowser/i.test(ua)) info.browser = 'Samsung Browser';
        else if (/Chrome|CriOS/i.test(ua)) info.browser = 'Chrome';
        else if (/Firefox|FxiOS/i.test(ua)) info.browser = 'Firefox';
        else if (/Safari/i.test(ua)) info.browser = 'Safari';
        else info.browser = 'Other';
    }

    cachedUaInfo = info;
    return info;
}

export function getBotName(): string | null {
    return getUserAgentInfo().bot ?? null;
}

export function isBot(): boolean {
    return getBotName() !== null;
}

export function getCleanReferrer(): string | null {
    if (!browser) return null;

    if (document.referrer) {
        try {
            const refUrl = new URL(document.referrer);
            const cleanRefHost = refUrl.hostname.replace(/^www\./i, '');
            const cleanCurrentHost = window.location.hostname.replace(/^www\./i, '');

            const isSameHost = cleanRefHost === cleanCurrentHost;
            if (isSameHost) {
                return cleanRefHost;
            }
            const pathAndQuery = (refUrl.pathname === '/' ? '' : refUrl.pathname) + refUrl.search;
            return cleanRefHost + pathAndQuery;
        } catch {
        }
    }

    try {
        const currentUrl = new URL(window.location.href);
        const params = currentUrl.searchParams;

        // Standard UTM parameters (e.g. ?utm_source=facebook&utm_medium=social)
        const utmSource = params.get('utm_source');
        const utmMedium = params.get('utm_medium');
        if (utmSource) {
            return utmMedium ? `${utmSource} (${utmMedium})` : utmSource;
        }

        // Common referral short-parameters (e.g. ?ref=producthunt or ?source=newsletter)
        const refParam = params.get('ref') || params.get('source');
        if (refParam) {
            return refParam;
        }

        // Platform Click IDs (Appended when users tap links on social apps)
        if (params.has('fbclid')) return 'facebook';
        if (params.has('gclid')) return 'google-ads';
        if (params.has('msclkid')) return 'bing-ads';
        if (params.has('twclid')) return 'twitter';
        if (params.has('ttclid')) return 'tiktok';
    } catch {
        return null;
    }

    return null;
}